import React, { useImperativeHandle } from "react";

import { useScrollEdges } from "emoreg/hooks";
import { Position } from "emoreg/layouts";
import { rem } from "polished";

import { TimePickerStyle } from "./styles";
import {
  computeTimeRangeValues,
  inputTimeToMinutes,
  minutesToDisplayTime,
  minutesToInputTime,
} from "./utils";

export type TimePickerProps = {
  /** classname, usable to overload component styling */
  className?: string;
  /** string value (format: HH:MM) */
  value?: string | null;
  /** value change handler (only expose new value) */
  onChangeValue?: (value: string) => void;
  /** min string value (format: HH:MM) */
  min?: string;
  /** max string value (format: HH:MM) */
  max?: string;
  /** time between two selectables values as string value (format: HH:MM) */
  step?: string;
  /** whether the time picker is disabled */
  disabled?: boolean;
};

export type TimePickerRefApi = {
  /** allow scrolling to a specific time (format: HH:MM) */
  scrollTo: (targetValue: string, behavior?: ScrollBehavior) => void;
};

export const TimePicker = React.forwardRef<TimePickerRefApi, TimePickerProps>(
  (
    {
      className,
      value,
      onChangeValue,
      min = "00:00",
      max = "23:59",
      step = "00:15",
      disabled = false,
    },
    ref
  ) => {
    // transform string input into amount of minutes
    const numberMin = inputTimeToMinutes(min);
    const numberMax = inputTimeToMinutes(max);
    const numberStep = inputTimeToMinutes(step);

    const { scrollerRef, isScrollAtTop, isScrollAtBottom, onScroll } =
      useScrollEdges<HTMLDivElement>();
    const options = computeTimeRangeValues(numberMin, numberMax, numberStep);

    // allow scrolling to a specific time (scroll center targets time when possible)
    const scrollTo = (value: string, behavior?: ScrollBehavior) => {
      if (!scrollerRef.current) return;
      const targetValue = inputTimeToMinutes(value);
      const minutesRange = numberMax - numberMin;
      // retrieve real world scroll values
      const scrollHeight = scrollerRef.current.scrollHeight;
      const clientHeight = scrollerRef.current.clientHeight;
      // scroll to expected position
      scrollerRef.current.scroll({
        top:
          (scrollHeight * (targetValue + numberStep / 2 - numberMin)) / minutesRange -
          clientHeight / 2,
        behavior,
      });
    };

    // expose scrollTo method to parent through ref
    useImperativeHandle(ref, () => ({ scrollTo }));

    return (
      <Position
        position="relative"
        className={className}
        w={rem(114)}
        h="100%"
        // arbitrary value to ensure that devs still see the
        // component event without applying style
        minH={rem(64)}
        pointerEvents={disabled ? "none" : "auto"}
        style={{ opacity: disabled ? 0.5 : 1 }}
      >
        <TimePickerStyle.Container
          ref={scrollerRef}
          direction="column"
          onScroll={onScroll}
          overflow="auto"
          gap={4}
        >
          {!isScrollAtTop && (
            <Position pointerEvents="none" position="sticky" zIndex={1} top={0} h={0}>
              <TimePickerStyle.ScrollIndicator
                reversed
                position="absolute"
                h={rem(64)}
                left={-1}
                right={-1}
                top={-1}
              />
            </Position>
          )}
          {options.map(option => {
            const inputTime = minutesToInputTime(option);
            return (
              <TimePickerStyle.Button
                key={inputTime}
                onClick={() => onChangeValue?.(inputTime)}
                checked={value === inputTime}
              >
                {minutesToDisplayTime(option)}
              </TimePickerStyle.Button>
            );
          })}
          {!isScrollAtBottom && (
            <Position pointerEvents="none" position="sticky" zIndex={1} bottom={0} h={0}>
              <TimePickerStyle.ScrollIndicator
                position="absolute"
                h={rem(64)}
                left={-1}
                right={-1}
                bottom={-1}
              />
            </Position>
          )}
        </TimePickerStyle.Container>
      </Position>
    );
  }
);
